// src/tabs/messages/ThreadDisplay.jsx — Phase 2.G.4 (ADR-031)
// Composant lecture seule : affiche les messages chronologiques d'un thread
// (bubbles inbound gauche / outbound droite) avec scroll auto au plus récent.
//
// Réutilisé depuis :
//   - InboxThread.jsx (avec composer en pied)
//   - ResaV2Modal.jsx onglet Messages (sans composer — l'admin va dans
//     l'Inbox principal pour répondre)

import { useRef, useEffect, useState } from "react"
import { MessageSquare, Mail, Sparkles, Check, Image as ImageIcon, Smartphone } from "lucide-react"
import { DS } from "../../lib/ui"
// Lot photos (Dimitri 2026-07-09) : extraction liste-blanche des images OTA
// du body BRUT (jamais modifié) — voir garde-fous dans inbox-media.js.
import { extractOtaImages } from "../../lib/inbox-media"

export default function ThreadDisplay({ messages, emptyMessage = "Aucun message dans cette conversation." }) {
  const scrollRef = useRef(null)
  const len = messages?.length || 0
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [len])

  if (!messages || len === 0) {
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"32px 16px",color:DS.textMuted,fontSize:13,background:DS.pageBg,borderRadius:8}}>
        <MessageSquare size={16} strokeWidth={2}/> {emptyMessage}
      </div>
    )
  }

  return (
    <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"14px 16px",background:DS.pageBg}}>
      {messages.map(m =>
        isSmsBubble(m)
          ? <SmsBubble key={m.id} m={m} />
          : isFormComment(m)
            ? <FormCommentBubble key={m.id} m={m} />
            : isDepartFeedback(m)
              ? <DepartFeedbackBubble key={m.id} m={m} />
              : isSystemEntry(m)
                ? <SystemLine key={m.id} m={m} />
                : <MessageBubble key={m.id} m={m} />,
      )}
    </div>
  )
}

// ── Entrées SYSTÈME (décision Dimitri 2026-07-09) ───────────────────────────
// Beds24 relaie certains événements OTA comme des « messages » du fil avec
// metadata.beds24_source='system' — ex. Airbnb poste « Review » quand le
// voyageur dépose son avis. Notre pull les stocke direction='outbound' (il ne
// connaît que guest/host), ce qui les faisait ressembler à un message de NOUS.
// Rendu voulu : une NOTIFICATION SYSTÈME NEUTRE — ligne centrée discrète au
// milieu du fil (façon « X a rejoint le groupe »), ni bulle voyageur ni bulle
// FBZ. Affichage seul : le body stocké n'est jamais modifié.
function isSystemEntry(m) {
  return m?.metadata?.beds24_source === "system"
}

// Libellés en clair des entrées système connues ; défaut = body brut (honnête
// pour un futur type d'entrée système inconnu).
function systemLineLabel(body) {
  if (String(body || "").trim().toLowerCase() === "review") {
    return "⭐ Le voyageur a laissé un avis"
  }
  return String(body || "").trim()
}

function SystemLine({ m }) {
  return (
    <div
      data-testid="msg-system-line"
      style={{display:"flex",justifyContent:"center",margin:"14px 0"}}
    >
      <span
        title="Notification automatique de la plateforme (via Beds24)"
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: DS.textMuted,
          background: DS.card,
          border: `1px solid ${DS.border}`,
          borderRadius: 999,
          padding: "4px 12px",
          textAlign: "center",
          maxWidth: "85%",
        }}
      >
        {systemLineLabel(m.body)}
      </span>
    </div>
  )
}

// ── Bulle « Commentaire du formulaire de bienvenue » (lot conversation
// enrichie) ─────────────────────────────────────────────────────────────────
// Entrée inbox canal='form_comment' insérée par le serveur à la soumission du
// formulaire de bienvenue (lib/form-submit-handlers.js) quand le champ
// commentaires est non vide. Rendu VOLONTAIREMENT distinct des bulles de
// conversation (fond + bordure pointillée + badge) — impossible à confondre
// avec un message plateforme. NON répondable comme un message plateforme :
// les sélecteurs lastInbound (InboxThread / ResaV2Modal) la sautent (pas de
// pseudo-Re:) — la réponse part comme un message NEUF. Le copilote IA VOIT
// cette entrée avec son contexte (marqueur dédié, buildThread) et peut
// proposer un brouillon à valider (amendement produit Dimitri).
function isFormComment(m) {
  return m?.canal === "form_comment"
}

function FormCommentBubble({ m }) {
  return (
    <div style={{display:"flex",justifyContent:"flex-start",marginBottom:10}}>
      <div
        data-testid="msg-form-comment"
        style={{
          maxWidth: "78%",
          background: DS.warningBg,
          color: DS.textPrimary,
          padding: "9px 13px",
          borderRadius: 12,
          border: `1.5px dashed ${DS.warning}`,
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        }}
      >
        <div style={{fontSize:10.5,fontWeight:700,marginBottom:4,color:DS.warningText,letterSpacing:.2}}>
          📝 Commentaire du formulaire de bienvenue
        </div>
        <div style={{fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.body}</div>
        <div style={{fontSize:9,marginTop:4,opacity:.6}}>
          {m.sender_name ? `${m.sender_name} · ` : ""}{formatBubbleTime(m.created_at)}
        </div>
      </div>
    </div>
  )
}

// ── Bulle « Ressenti de fin de séjour » (système d'avis « option a ») ────────
// Entrée inbox canal='depart_feedback' insérée par le serveur (lib/feedback-
// handlers.js) quand un voyageur donne un ressenti NÉGATIF (< seuil) et laisse
// un commentaire. Rendu VOLONTAIREMENT distinct (fond + bordure corail) — une
// PLAINTE de départ à traiter, pas un message plateforme. NON répondable comme
// un message plateforme : les sélecteurs lastInbound (InboxThread /
// ResaV2Modal) la sautent (la réponse part comme un message NEUF). RÉSERVÉE À
// L'ADMIN : le serveur (lib/proprio-handlers.js) ne la sert JAMAIS au proprio.
// Le copilote IA la VOIT (marqueur dédié, buildThread) avec une consigne de
// formulation empathique. Discriminant : canal='depart_feedback'.
function isDepartFeedback(m) {
  return m?.canal === "depart_feedback"
}

function DepartFeedbackBubble({ m }) {
  return (
    <div style={{display:"flex",justifyContent:"flex-start",marginBottom:10}}>
      <div
        data-testid="msg-depart-feedback"
        style={{
          maxWidth: "78%",
          background: DS.dangerBg || DS.warningBg,
          color: DS.textPrimary,
          padding: "9px 13px",
          borderRadius: 12,
          border: `1.5px dashed ${DS.danger || DS.corail || DS.warning}`,
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        }}
      >
        <div style={{fontSize:10.5,fontWeight:700,marginBottom:4,color:DS.dangerText || DS.warningText,letterSpacing:.2}}>
          ⚠️ Ressenti de fin de séjour à traiter
        </div>
        <div style={{fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.body}</div>
        <div style={{fontSize:9,marginTop:4,opacity:.6}}>
          {m.sender_name ? `${m.sender_name} · ` : ""}{formatBubbleTime(m.created_at)}
        </div>
      </div>
    </div>
  )
}

// ── Bulle « SMS » (lot « SMS dans le fil ») ─────────────────────────────────
// Entrée inbox canal='sms' : trace de VISIBILITÉ d'un SMS envoyé au voyageur —
// transactionnel Direct (lib/journey-comms.js, lib/direct-journey-sweep.js),
// marketing OTA (lib/marketing-sms.js), ou réponse manuelle admin
// (api/send-email.js inbox_reply). RÉSERVÉE À L'ADMIN : le serveur
// (lib/proprio-handlers.js) NE sert JAMAIS les entrées canal='sms' au proprio
// (même masquage que form_comment) + ceinture client. Rendu VOLONTAIREMENT
// distinct de la bulle plateforme (fond marine plein) ET de la bulle
// form_comment (fond ambre pointillé) : fond ciel doux + bordure ciel pleine +
// badge 📱 — reconnaissable d'un coup d'œil. Discriminant : canal='sms'.
// SORTANT (direction='outbound') = aligné à droite, « SMS envoyé le … ». Le
// canal SMS FBZ est UNIDIRECTIONNEL (expéditeur alphanumérique) → aucun SMS
// ENTRANT n'arrive aujourd'hui ; le style entrant est néanmoins prévu (aligné à
// gauche, « SMS reçu le … ») pour un futur canal bidirectionnel.
function isSmsBubble(m) {
  return m?.canal === "sms"
}

// ⚠️ UN MESSAGE SIMULÉ N'EST PAS UN MESSAGE ENVOYÉ. En mode essai
// (MESSAGES_DRY_RUN), le serveur enregistre la réponse avec
// `metadata.dry_run: true` mais n'envoie RIEN au voyageur. La dessiner comme
// une bulle partie laissait croire à Dimitri que le voyageur l'avait reçue.
// Règle : aucune bulle ne se dit « envoyée » si rien n'est réellement parti.
export function estSimule(m) {
  return m?.direction !== "inbound" && m?.metadata?.dry_run === true
}

// L'ÉTAT D'UN ENVOI RÉSERVÉ (29/09, point 3) : une réponse envoyée depuis
// l'application est enregistrée AVANT l'envoi (« en_cours »), puis marquée
// « parti », « echec_net » ou « incertain ». Seul « parti » (ou l'absence de
// marque, pour l'ancien chemin) est un envoi : le reste le dit.
//
// N3 revue (29/09) : une réservation « en_cours » depuis plus de 2 minutes est
// un traitement mort en route — le message est peut-être parti. Elle se lit
// « incertain », comme côté serveur (lib/inbox-envoi-statut.js
// `etatEnvoiLu`, même délai) : jamais « en cours », qui dirait d'attendre.
export const DELAI_EN_COURS_MS = 2 * 60 * 1000

export function etatEnvoi(m, maintenant = Date.now()) {
  if (!m || m.direction === "inbound") return null
  const meta = m.metadata || {}
  const e = meta.envoi_statut
  if (e === "en_cours") {
    const depuis = Date.parse(meta.envoi_en_cours_le || m.created_at || "")
    return Number.isFinite(depuis) && maintenant - depuis > DELAI_EN_COURS_MS ? "incertain" : e
  }
  return e === "echec_net" || e === "incertain" ? e : null
}

const LIBELLES_ETAT = {
  en_cours: "Envoi en cours…",
  echec_net: "Non parti — renvoyez-le depuis la réponse",
  incertain: "Envoi incertain — vérifiez la conversation avant de renvoyer",
}

function BandeauEtat({ etat }) {
  return (
    <div data-testid="msg-etat-envoi" style={{fontSize:10.5,fontWeight:700,marginBottom:4,color:etat === "en_cours" ? DS.textSecondary : DS.dangerText,letterSpacing:.2}}>
      {LIBELLES_ETAT[etat]}
    </div>
  )
}

function BandeauSimule() {
  return (
    <div data-testid="msg-simule" style={{display:"flex",alignItems:"center",gap:5,fontSize:10.5,fontWeight:700,marginBottom:4,color:DS.warningText,letterSpacing:.2}}>
      Simulé — non envoyé au voyageur (mode essai)
    </div>
  )
}

function SmsBubble({ m }) {
  const isInbound = m.direction === "inbound"
  const simule = estSimule(m)
  const etat = etatEnvoi(m)
  const align = isInbound ? "flex-start" : "flex-end"
  const label = isInbound
    ? "SMS reçu"
    : simule
      ? "SMS simulé, non envoyé,"
      : etat === "en_cours"
        ? "SMS en cours d'envoi,"
        : etat
          ? "SMS NON parti,"
          : "SMS envoyé"
  return (
    <div style={{display:"flex",justifyContent:align,marginBottom:10}}>
      <div
        data-testid="msg-sms"
        style={{
          maxWidth: "78%",
          background: DS.cielSoft,
          color: DS.textPrimary,
          padding: "9px 13px",
          borderRadius: 12,
          border: simule ? `1.5px dashed ${DS.warning}` : `1.5px solid ${DS.ciel}`,
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        }}
      >
        <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10.5,fontWeight:700,marginBottom:4,color:DS.marine,letterSpacing:.2}}>
          <Smartphone size={12} strokeWidth={2}/> 📱 {label} le {formatSmsTime(m.created_at)}
        </div>
        {m.body && <div style={{fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.body}</div>}
      </div>
    </div>
  )
}

// Horodatage bulle SMS : « JJ/MM à HHhMM » (FR), aligné sur le libellé attendu
// « SMS envoyé le JJ/MM à HHh ».
function formatSmsTime(ts) {
  if (!ts) return ""
  const d = new Date(ts)
  const dd = String(d.getDate()).padStart(2, "0")
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const hh = String(d.getHours()).padStart(2, "0")
  const min = String(d.getMinutes()).padStart(2, "0")
  return `${dd}/${mm} à ${hh}h${min}`
}

// Image OTA (liste blanche) : miniature cliquable → original dans un onglet ;
// les URLs Airbnb signées EXPIRENT → onError bascule sur un lien propre
// « Photo (ouvrir) », jamais une icône cassée.
function OtaImage({ url }) {
  const [broken, setBroken] = useState(false)
  if (broken) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,fontWeight:600,textDecoration:"underline",color:"inherit",opacity:.9}}>
        <ImageIcon size={13} strokeWidth={2}/> Photo (ouvrir)
      </a>
    )
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Ouvrir la photo en grand" style={{display:"block"}}>
      <img src={url} alt="Photo envoyée par le voyageur" loading="lazy"
        onError={() => setBroken(true)}
        style={{maxWidth:"100%",maxHeight:280,borderRadius:10,display:"block",objectFit:"cover"}}/>
    </a>
  )
}

function MessageBubble({ m }) {
  const isInbound = m.direction === 'inbound'
  const simule = estSimule(m)
  const etat = etatEnvoi(m)
  const pasParti = simule || !!etat
  const align = isInbound ? 'flex-start' : 'flex-end'
  // Simulé ou pas (encore) parti : jamais le marine plein d'un vrai envoi.
  const bg = isInbound || pasParti ? DS.card : DS.marine
  const fg = isInbound || pasParti ? DS.textPrimary : '#fff'
  const borderColor = isInbound ? DS.border : etat && etat !== "en_cours" ? DS.danger : pasParti ? DS.warning : DS.marine
  // Affichage seul : le body STOCKÉ reste brut (copilote IA/recherche intacts).
  const { images, text } = extractOtaImages(m.body)
  return (
    <div style={{display:"flex",justifyContent:align,marginBottom:10}}>
      <div data-testid="msg-bubble" style={{maxWidth:"78%",background:bg,color:fg,padding:"9px 13px",borderRadius:12,border:pasParti?"1.5px dashed":"1px solid",borderColor,boxShadow:"0 1px 3px rgba(0,0,0,.04)"}}>
        {simule && <BandeauSimule/>}
        {etat && <BandeauEtat etat={etat}/>}
        {m.subject && <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,marginBottom:4,opacity:.85}}><Mail size={12} strokeWidth={2}/> {m.subject}</div>}
        {images.map((url) => (
          <div key={url} data-testid="msg-ota-image" style={{margin:"2px 0 6px"}}>
            <OtaImage url={url}/>
          </div>
        ))}
        {text && <div style={{fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{text}</div>}
        <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,marginTop:4,opacity:.6,justifyContent:isInbound?'flex-start':'flex-end',flexWrap:"wrap"}}>
          {formatBubbleTime(m.created_at)}
          {!isInbound && m.related_send_id && <span title="Lié à un envoi automatique" style={{display:"inline-flex",alignItems:"center",gap:3}}>· <Sparkles size={10} strokeWidth={2}/> auto</span>}
          {isInbound && m.read_at && <span style={{display:"inline-flex",alignItems:"center",gap:3}}>· <Check size={10} strokeWidth={2.4}/> lu</span>}
        </div>
      </div>
    </div>
  )
}

function formatBubbleTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}
